<!-- src/pages/app/feedback/preference/[id].vue -->
<script setup>
import PreferenceService from '@/services/preference.service'
import PatientSidebar from '@/views/feedback/preference/PatientSidebar.vue'
import PreferenceActivityDialog from '@/views/feedback/preference/PreferenceActivityDialog.vue'
import PreferenceActivityLog from '@/views/feedback/preference/PreferenceActivityLog.vue'
import PreferenceCard from '@/views/feedback/preference/PreferenceCard.vue'
import PreferenceDialog from '@/views/feedback/preference/PreferenceDialog.vue'

definePage({
  meta: {
    subject: 'feedback.menu',
    action: 'preference',
  },
})

const route = useRoute()
const router = useRouter()
const { t } = useI18n()

const customerId = computed(() => route.params.id)

// Fetch customer preferences using customer_id search param
const queryParams = computed(() => ({
  search: {
    customer_id: customerId.value,
  },
}))

const { data: customerData, isFetching, execute: refreshPreferences } = await useApi(
  createUrl('/feedbacks/preferences/customers/' + customerId.value),
)

const customer = computed(() => customerData.value?.customer ?? null)
const currentEpisode = computed(() => customerData.value?.current_episode ?? null)
const episodes = computed(() => customerData.value?.episodes ?? [])
const preferences = computed(() => customerData.value?.preferences ?? [])
const deletedPreferences = computed(() => customerData.value?.deleted_preferences ?? [])

// Department tabs
const activeDepartmentTab = ref('all')

const departmentTabs = computed(() => {
  const grouped = {}

  preferences.value.forEach(pref => {
    const deptName = pref.department?.name ?? 'Other'
    const deptId = pref.department_id ?? 'other'

    if (!grouped[deptId]) {
      grouped[deptId] = { name: deptName, id: deptId, count: 0, hasRecentUpdate: false }
    }
    grouped[deptId].count++

    // Check for recent updates (within last 24 hours)
    const updatedAt = new Date(pref.updated_at)
    const oneDayAgo = new Date(Date.now() - 86400000)

    if (updatedAt > oneDayAgo) {
      grouped[deptId].hasRecentUpdate = true
    }
  })

  const tabs = [
    { name: 'All', id: 'all', count: preferences.value.length, hasRecentUpdate: false },
    ...Object.values(grouped),
  ]

  if (deletedPreferences.value.length > 0) {
    tabs.push({ name: 'Deleted', id: 'deleted', count: deletedPreferences.value.length, hasRecentUpdate: false, color: 'error' })
  }

  return tabs
})

const filteredPreferences = computed(() => {
  if (activeDepartmentTab.value === 'deleted') return deletedPreferences.value
  if (activeDepartmentTab.value === 'all') return preferences.value

  return preferences.value.filter(
    pref => String(pref.department_id) === String(activeDepartmentTab.value),
  )
})

// Preference Dialog
const isPreferenceDialogVisible = ref(false)
const editingPreference = ref(null)

const openAddDialog = () => {
  editingPreference.value = null
  isPreferenceDialogVisible.value = true
}

const openEditDialog = preference => {
  editingPreference.value = preference
  isPreferenceDialogVisible.value = true
}

const onPreferenceSaved = () => {
  refreshPreferences()
}

// Activity Dialog
const isActivityDialogVisible = ref(false)
const activityPreferenceId = ref(null)
const activityPreferenceTitle = ref('')

const showActivityDialog = preference => {
  activityPreferenceId.value = preference.id
  activityPreferenceTitle.value = preference.title
  isActivityDialogVisible.value = true
}

const acknowledgePreference = async preference => {
  await PreferenceService.acknowledge(
    preference.id,
    {},
    () => {
      refreshPreferences()
    },
  )
}

const restorePreference = async preference => {
  await PreferenceService.restore(
    preference.id,
    () => {
      refreshPreferences()
    },
  )
}

const deletePreference = async preference => {
  await PreferenceService.destroy(
    preference.id,
    () => {
      refreshPreferences()
    },
  )
}

const isExporting = ref(false)

const exportPreferences = async () => {
  isExporting.value = true

  const url = createUrl('feedbacks/preferences/export', {
    query: {
      customer_id: customerId.value,
    },
  })

  await $api(url.value).then(async () => {
    await new Promise(resolve => setTimeout(resolve, 2000))
  }).catch(() => {})

  isExporting.value = false
}

const goBack = () => {
  router.push({ name: 'app-feedback-preference' })
}
</script>

<template>
  <div>
    <!-- Header -->
    <div class="d-flex align-center justify-space-between flex-wrap gap-3 mb-6">
      <div class="d-flex align-center gap-3">
        <IconBtn
          variant="tonal"
          color="primary"
          rounded
          @click="goBack"
        >
          <VIcon icon="tabler-arrow-left" />
          <VTooltip
            activator="parent"
            location="bottom"
          >
            Back to patients
          </VTooltip>
        </IconBtn>
        <div>
          <h4 class="text-h5 font-weight-bold">
            {{ customer?.name }}
          </h4>
          <span class="text-body-2 text-medium-emphasis d-none d-sm-inline">
            {{ customer?.code }}
            <template v-if="customer?.age">
              · {{ customer.age }}y
            </template>
            <template v-if="customer?.gender">
              · {{ customer.gender }}
            </template>
            <template v-if="currentEpisode?.location?.name">
              · {{ currentEpisode.location.name }}
            </template>
          </span>
          <span class="text-body-2 text-medium-emphasis d-inline d-sm-none">
            {{ customer?.code }}
          </span>
        </div>
      </div>
      <div class="d-flex gap-3">
        <!-- Export -->
        <template v-if="$can('export', 'feedback.preference')">
          <VBtn
            variant="tonal"
            color="primary"
            prepend-icon="tabler-download"
            :loading="isExporting"
            class="d-none d-sm-flex"
            @click="exportPreferences"
          >
            Export
          </VBtn>
          <IconBtn
            variant="tonal"
            color="primary"
            :loading="isExporting"
            class="d-flex d-sm-none"
            @click="exportPreferences"
          >
            <VIcon icon="tabler-download" />
          </IconBtn>
        </template>

        <!-- Add Preference -->
        <template v-if="$can('create', 'feedback.preference')">
          <VBtn
            prepend-icon="tabler-plus"
            variant="flat"
            class="d-none d-sm-flex"
            @click="openAddDialog"
          >
            Add Preference
          </VBtn>
          <VBtn
            icon="tabler-plus"
            variant="flat"
            size="small"
            class="d-flex d-sm-none"
            @click="openAddDialog"
          />
        </template>
      </div>
    </div>

    <!-- Loading State -->
    <div
      v-if="isFetching"
      class="d-flex justify-center pa-12"
    >
      <VProgressCircular indeterminate />
    </div>

    <!-- Content -->
    <VRow v-else>
      <!-- Left: Preferences -->
      <VCol
        cols="12"
        md="8"
        order="1"
        order-md="1"
      >
        <VCard
          variant="flat"
          class="border"
        >
          <!-- Department Tabs -->
          <VTabs
            v-model="activeDepartmentTab"
            class="mt-1"
            bg-color="transparent"
            show-arrows
          >
            <VTab
              v-for="tab in departmentTabs"
              :key="tab.id"
              :value="tab.id"
              :color="tab.color"
            >
              {{ tab.name }}
              <VChip
                size="x-small"
                variant="tonal"
                :color="tab.color ?? 'primary'"
                rounded
                class="ms-1"
              >
                {{ tab.count }}
              </VChip>
            </VTab>
          </VTabs>

          <!-- <VDivider /> -->

          <!-- Preference Cards -->
          <VCardText class="d-flex flex-column gap-0 pa-0">
            <div
              v-for="(preference, preferenceIndex) in filteredPreferences"
              :key="preference.id"
            >
              <PreferenceCard
                :preference="preference"
                @edit="openEditDialog"
                @show-activity="showActivityDialog"
                @acknowledge="acknowledgePreference"
                @delete="deletePreference"
                @restore="restorePreference"
              />

              <VDivider
                v-if="preferenceIndex < filteredPreferences.length - 1"
                class="ma-0"
              />
            </div>

            <!--
              <PreferenceCard
              v-for="preference in filteredPreferences"
              :key="preference.id"
              :preference="preference"
              @edit="openEditDialog"
              @show-activity="showActivityDialog"
              @acknowledge="acknowledgePreference"
              /> 
            -->

            <!-- Empty State -->
            <div
              v-if="!filteredPreferences.length"
              class="text-center pa-8 text-medium-emphasis"
            >
              <VIcon
                icon="tabler-settings-off"
                size="48"
                class="mb-3"
              />
              <p class="text-body-1">
                No preferences recorded yet.
              </p>
              <VBtn
                variant="outlined"
                prepend-icon="tabler-plus"
                class="mt-2"
                @click="openAddDialog"
              >
                Add Preference
              </VBtn>
            </div>
          </VCardText>
        </VCard>
      </VCol>

      <!-- Activity Log Section -->
      <VCol
        cols="12"
        md="8"
        order="2"
        order-md="3"
      >
        <PreferenceActivityLog :customer-id="customerId" />
      </VCol>

      <!-- Right: Sidebar -->
      <VCol
        cols="12"
        md="4"
        order="3"
        order-md="2"
      >
        <PatientSidebar
          :customer="customer"
          :current-episode="currentEpisode"
          :episodes="episodes"
        />
      </VCol>
    </VRow>

    <!-- Dialogs -->
    <PreferenceDialog
      v-model:is-dialog-visible="isPreferenceDialogVisible"
      :preference-data="editingPreference"
      :customer-id="customerId"
      :episode-id="currentEpisode?.id"
      @saved="onPreferenceSaved"
    />

    <PreferenceActivityDialog
      v-model:is-dialog-visible="isActivityDialogVisible"
      :preference-id="activityPreferenceId"
      :preference-title="activityPreferenceTitle"
    />
  </div>
</template>
